import { Button, Grid2, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { CenteredLazyLoading } from "../../../../../components/shared/CenteredLazyLoading";
import { useContextMenuMESF } from "../../../../../controls/contextMenu";
import { themeDXT } from "../../../../../css/themeMESF";
import { Profile } from "../../models/Profile";
import { getProfiles } from "../../repositories/ProfilesRepository";
import { CloneProfile } from "../Clone/CloneProfile";
import { CreateProfile } from "../Create/CreateProfile";
import { DeleteProfile } from "../Delete/DeleteProfile";
import { ActionsOfProfile } from "../ProceduresOfProfile/ActionsOfProfile";
import { useProfilesOptionsFunctions } from "./customHooks/useProfilesOptionsFunctions";
import { useTableData } from "./customHooks/useTableData";

const useSearchProfiles = () => {
	return useQuery<Profile[], Error>({
		queryKey: ["profiles"],
		queryFn: ({ signal }) => getProfiles(signal),
	});
};

export const TableProfiles = () => {
	const [modalCreateActive, setModalCreateActive] = useState(false);
	const [modalProceduresProfile, setModalProceduresProfile] = useState(false);
	const [modalDeleteProfile, setModalDeleteProfile] = useState(false);
	const [modalCloneProfile, setModalCloneProfile] = useState(false);
	const [profile, setProfile] = useState<Profile | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState("");

	const {
		data: rows,
		isLoading,
		isError,
		error: e,
		refetch,
	} = useSearchProfiles();

	const formattedRows = rows?.map(({ ProfileId, ProfileName }: Profile) => ({
		id: ProfileId,
		ProfileId,
		ProfileName: ProfileName,
	}));

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
			suppressHeaderMenuButton: true,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
		};
	}, []);

	const { getMenuOptions } = useProfilesOptionsFunctions({
		setProfileId: setProfile,
		setShowCreateModal: setModalCreateActive,
		setShowDeleteModal: setModalDeleteProfile,
		setShowCloneModal: setModalCloneProfile,
		setModalProceduresProfile,
	});
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs } = useTableData({
		setModalDeleteProfile,
		setModalProceduresProfile,
		setProfileId: setProfile,
		showContextMenu,
	});
	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (DefaultMenuItem | MenuItemDef)[] => {
		const data = params.node?.data;
		params.api.deselectAll();
		params.node?.setSelected(true);
		showContextMenu(event as any, data, "TableProfiles");
		return [];
	};

	useEffect(() => {
		registerConfig({
			id: "TableProfiles",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Profile Management
					</Typography>
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					<Grid2
						size={{ md: 12, xs: 12 }}
						component={Paper}
						style={{ height: "70vh" }}
					>
						<AgGridReact
							loading={isLoading}
							gridOptions={{
								theme: themeDXT,
							}}
							rowData={formattedRows || []}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							rowHeight={34}
							headerHeight={34}
							loadingOverlayComponent={CenteredLazyLoading}
							animateRows
							getContextMenuItems={(e: any) => getContextMenuItems(e)}
							getRowId={(params) => params.data.ProfileId}
							rowSelection="single"
							onRowDoubleClicked={(event) => {
								setProfile(event.data);
								setModalProceduresProfile(true);
							}}
							onGridReady={(params) => setGridApi(params.api)}
						/>
					</Grid2>
					<Grid2 container justifyContent="flex-end">
						<Grid2 size={{ md: 2, xs: 12 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => setModalCreateActive(!modalCreateActive)}
								fullWidth
							>
								NEW PROFILE
							</Button>
						</Grid2>
					</Grid2>
				</Grid2>
			</Grid2>

			<CreateProfile
				show={modalCreateActive}
				onHide={(shouldUpdate) => {
					setModalCreateActive(false);
					if (shouldUpdate) refetch();
				}}
				suffixTitle="Profile Management"
			/>
			<DeleteProfile
				show={modalDeleteProfile}
				profile={profile}
				onHide={(shouldUpdate: boolean) => {
					setModalDeleteProfile(false);
					setProfile(null);
					if (shouldUpdate) refetch();
				}}
				suffixTitle="Profile Management"
			/>
			<ActionsOfProfile
				show={modalProceduresProfile}
				profileForEdit={profile}
				onHide={(shouldUpdate: boolean) => {
					setModalProceduresProfile(false);
					setProfile(null);
					if (shouldUpdate) refetch();
				}}
				suffixTitle="Profile Management"
			/>
			<CloneProfile
				show={modalCloneProfile}
				profile={profile}
				onHide={() => {
					setModalCloneProfile(false);
					setProfile(null);
				}}
				suffixTitle="Profile Management"
			/>
		</>
	);
};
